/**
 * /product-roadmap-example 的中文变体。结构、className、调性对齐
 * ProductRoadmapExample.tsx，母语级中文(非翻译腔)。
 *
 * 切入角度:搜「产品路线图示例 / product roadmap example」的人想要的是
 * 格式 —— Now-Next-Later、时间轴/甘特、主题型、结果型、版本型。先把这几种
 * 真东西给足(每种一句示例 + 何时用),再点破共同的坑:路线图是优先级的
 * 快照,而优先级就是决策(定价、范围、排期、不做什么);一旦放进 slide
 * 或表格,就和这些决策脱钩了。改一次优先级,没人同步,路线图就成了
 * 没人对齐的虚构。Draftlize 保留同样的格式,把每个条目连到它依赖的决策,
 * 决策一变,依赖它的条目自动标 stale。
 */
import { MarketingShell } from './MarketingShell';

export function ProductRoadmapExampleZh() {
  return (
    <MarketingShell
      currentLocale="zh"
      hreflangPath="/product-roadmap-example"
      documentTitle="产品路线图示例(不会过期的那种)· Draftlize"
      documentDescription="五种可以直接抄的产品路线图示例 —— Now-Next-Later、时间轴/甘特、主题型、结果型、版本型,每种附一句何时用。但坑在于:每张路线图都是脱离了底层决策的快照,优先级改一次它就错了。在 Draftlize 里,每个路线图条目都连着它依赖的决策,上游决策一变,条目自动标 stale。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">示例 · 产品路线图</span>
          <h1 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1, margin: '12px 0 24px' }}>
            几种产品路线图示例 ——<br/>
            <em>以及会自己更新的那种。</em>
          </h1>
          <p className="lede">
            你是来找<em>产品路线图示例</em>的 —— 一个能抄的格式,一种能讲清楚「什么时候交付什么」的结构。下面有五种好用的,每种配一句示例和它最适合的场景,挑一种和你团队思路对得上的拿走就行。但它们都有一个格式本身修不掉的毛病:路线图是优先级的一张画面,而优先级本质上是<em>决策</em> —— 定价怎么定、范围砍到哪、先做哪个、什么干脆不做。把画面放进 slide 或表格,存盘那一刻它就和这些决策脱了钩。优先级改一次,路线图立刻变成没人同步的虚构。Draftlize 保留同样的格式,把每个条目连到它底下的决策;决策一动,依赖它的条目就自己标成 stale。
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">示例</span>
            <h2 className="serif">五种格式。<em>挑一个对得上的。</em></h2>
          </div>
          <p>这就是团队真正在用的几种产品路线图示例。每一种放在文档、slide 或表格里都能用 —— 挑一个和你规划方式对得上的抄走。下一节讲的是:当每个条目不再只是 slide 上一个方块、而是带着它所依赖的决策时,会发生什么变化。</p>
        </div>

        <div
          style={{
            border: '1px solid var(--ink-0)',
            background: 'var(--paper-0)',
            fontFamily: "'Inter', sans-serif",
          }}
        >
          {[
            {
              k: 'Now · Next · Later',
              hint: '默认首选',
              body: '用三个桶代替日期。Now 是正在做的,Next 是已经定下来的,Later 是大方向。示例:Now —— SSO + 审计日志;Next —— 按量计费;Later —— 移动端。何时用:早期阶段,或者任何真的还说不准的东西 —— 硬给一个三月的日期只是一个你日后要还的谎。最诚实的默认选项。',
            },
            {
              k: '时间轴 / 甘特',
              hint: '排日期',
              body: '把各项工作铺在日历上,带条形和依赖关系。示例:Q1 —— 计费重构(1–2 月),接着 SSO(2–3 月);Q2 —— 移动端 beta。何时用:有硬性外部截止、发布绑了合同或活动、或者需要让相关方看清排期。强大也危险 —— 那些日期会被当成承诺。',
            },
            {
              k: '主题型',
              hint: '讲策略',
              body: '按战略主题而非功能来组织。示例:「缩短上手时间」「拿下企业客户」「压低基础设施成本」 —— 每个主题下挂着服务于它的具体工作。何时用:需要让管理层和团队先在「为什么」上对齐再谈「做什么」,并且想保留在一个主题下换功能、而不必重谈整张计划的余地。',
            },
            {
              k: '结果型',
              hint: '看指标',
              body: '对结果负责,而不是对产出负责。示例:「激活率从 28% 提到 40%」「把 p95 延迟压到 200ms 以内」 —— 功能只作为奔向每个数字的下注列在下面。何时用:成熟产品、有它信得过的指标,团队被允许只要结果不变就换打法。这是最能抵御「功能流水线」漂移的格式。',
            },
            {
              k: '版本型',
              hint: '排版本',
              body: '围绕发布里程碑来组织。示例:v2.1 —— SAML + SCIM;v2.2 —— 按量计费;v3.0 —— 工作区改版。何时用:有版本号的产品、任何带 changelog 或发布说明的东西、或者用户会围着你的版本做规划的平台。和用户实际拿到的东西一一对得上。',
            },
          ].map((f, i) => (
            <div
              key={f.k}
              style={{
                display: 'grid',
                gridTemplateColumns: '220px 1fr',
                gap: 24,
                padding: '20px 22px',
                borderTop: i === 0 ? 0 : '1px dashed var(--rule)',
                alignItems: 'baseline',
              }}
            >
              <div>
                <span
                  className="serif"
                  style={{ fontSize: 19, fontWeight: 500, color: 'var(--ink-0)', display: 'block', lineHeight: 1.2 }}
                >
                  {f.k}
                </span>
                <span
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 9.5,
                    letterSpacing: '.1em',
                    textTransform: 'uppercase',
                    color: 'var(--amber)',
                    fontWeight: 700,
                  }}
                >
                  {f.hint}
                </span>
              </div>
              <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.6, color: 'var(--ink-1)' }}>{f.body}</p>
            </div>
          ))}
        </div>

        <p
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 11,
            letterSpacing: '.06em',
            color: 'var(--ink-3)',
            textAlign: 'center',
            marginTop: 18,
          }}
        >
          这就是路线图 —— 用哪种格式都行。下面讲:为什么优先级一动它就开始烂掉 —— 以及我们怎么办。
        </p>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">为什么路线图<em>会过期。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">问题不在格式,在脱钩</h3>
            <p>Now-Next-Later 也好,甘特图也好,形状本身没毛病。真正害死路线图的是:它是一组决策的渲染图,却没跟这些决策连在一起。每个条目背后的「为什么」 —— 定价的拍板、范围的取舍、排期的权衡 —— 要么住在别处、要么哪都不在,slide 上只剩一个结论。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">改一次优先级,没人同步</h3>
            <p>一笔交易逼着你把「计费」从 Next 提到 Now。这一个改动会扩散:发布顺序、依赖它的若干 spec、三个假定了旧顺序的下游条目。在表格里它们一个都不知道。周一汇报的路线图,周三就已经错了,而且这种错是看不见的。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">它是个交付物,不是事实源</h3>
            <p>放在 slide 里的路线图,存在的意义就是被拿来讲一次,然后一直过期到下次有人为了下一场评审手动重建它。两次汇报之间,它和真正在做的东西渐行渐远,于是所有人都悄悄学会了:信站会,别信路线图。</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">同一张路线图,<em>让它活起来。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">每个条目都带着它所依赖的决策</h3>
            <p>在 Draftlize 里,路线图条目不是 slide 上一个方块 —— 它是一张卡片,连着底下的决策:它所假定的定价拍板、它被砍到的范围、它依赖的排期。点开条目,你看到的是「为什么」,而不只是「做什么」。格式仍然是 Now-Next-Later 或版本型;新的是底下那套接线。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">改一个决策,依赖它的条目自动标 stale</h3>
            <p>因为每个条目连的是真实的决策、而不只是一个日期,改动一个决策,就会让所有依赖它的路线图条目自动变 stale —— 就像构建系统会让一个被改文件的所有下游全部失效。挪一下定价,那些假定了旧价格的条目当场举手,而不是等到 demo 上让你下不来台。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">AI agent 能读写这张路线图</h3>
            <p>Claude Code 或 Cursor,通过 MCP,在起草下一份 spec 之前先读完每个路线图条目背后的决策,再把新条目和新决策按同样的结构写回去。路线图不再是为每场评审重建一次的 slide,而是变成每一轮都会被读到、并且在两次评审之间一直保持为真的上下文。</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          路线图是你优先级的一张画面。优先级就是决策 —— 而一张画面没法告诉你,其中哪一个已经变了。
        </blockquote>
        <cite>留住你喜欢的格式,让底层替你保持它常新。</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">常见问题</span>
            <h2 className="serif">FAQ。</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: '一份好的产品路线图长什么样？', a: '好的路线图展示的是随时间推进的结果和主题，而不是一串带日期的功能列表——常见格式有 Now-Next-Later、时间线、或按 release。最好的那种把「为什么」显出来：每一项都回连到策略和它所依赖的决策，于是读者看到的是优先级，而不只是一个个格子。' },
            { q: '产品路线图主要有哪几种？', a: '常见格式是 Now-Next-Later（越往远看，确定性越低）、时间线或甘特式（按日历，日期确定时最合适）、主题式（按结果或问题组织）、按 release（按上线分组）。按你真正拥有的确定性来选——一条你守不住的精确时间线，比一份诚实的 Now-Next-Later 更糟。' },
            { q: '产品路线图应该包含什么？', a: '主题或结果、它们底下的项、大致的时间段或时段、负责人，以及最好每一项服务的目标。它不该是一份带日期的 backlog——路线图传达的是方向和优先级，硬塞满承诺日期，就把它变成一个你会违背的承诺。' },
            { q: '怎么让路线图在两次评审之间不过时？', a: '一份为每次评审手工重建的路线图，会在会议一结束就漂移。Draftlize 把每一项连到它底下的决策，于是改一个决策，凡是建立在它之上的项都自动标成 stale——路线图在两次评审之间保持为真，而不是被信任得还不如站会。' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.7, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">做一张始终为真的路线图,<em>注册送 $5 起步。</em></h2>
        </div>
        <div className="trust-strip">
          <span>新账号送 $5</span>
          <span>按量付费,用多少付多少</span>
          <span>余额永不过期</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            上面任一格式都能直接抄,或者开个项目,让 agent 从你现有的文档里把路线图搭出来 —— 然后替你把每个条目、以及它依赖的决策,一直保持同步。
          </p>
          <a className="btn-primary" href="/">注册送 $5,免费开始</a>
        </div>
      </section>
    </MarketingShell>
  );
}
